// ---------------------------------------------------------------------------
// Discovery Constants
// ---------------------------------------------------------------------------
// Sort field maps and defaults used by FeltonDiscovery page.
// ---------------------------------------------------------------------------

import type { DataSourceId } from '@/components/v11/discovery'

export const DEFAULT_PAGE_SIZE = 25

export const DEFAULT_SORT = 'id'

// Default sort field per datasource
export const DEFAULT_SORT_BY_SOURCE: Record<DataSourceId, string> = {
  profiles: 'total_comments',
  content: 'post_date',
  comments: 'id',
  brands: 'profiles',
  hashtags: 'id',
  teams: 'profiles',
  channels: 'id',
}

// Map frontend column IDs to backend sort field names (by datasource)
export const COLUMN_TO_SORT_FIELD_MAP: Record<DataSourceId, Record<string, string>> = {
  profiles: {
    id: 'id',
    // Metrics
    followers: 'followers_count',
    username: 'username',
    sentiment: 'sentiment',
    posts: 'total_posts',
    comments: 'total_comments',
    brands: 'total_brands',
    teams: 'total_teams',
    // Demographics
    country: 'country_name',
    state: 'state_name',
    platform: 'platform_name',
    gender: 'gender_name',
    age: 'age_interval_name',
    emotion: 'dominant_emotion',
    emotionCategory: 'emotion_category_name',
    verified: 'is_verified',
    famous: 'is_famous',
    isPerson: 'is_person',
    processed: 'has_analysis',
    ethnicity: 'ethnicity_name',
    lifestyle: 'lifestyle_name',
    industry: 'industry_name',
    followerTier: 'follower_tier_name',
    city: 'city_name',
    continent: 'continent_name',
    statusClass: 'status_class_name',
    petFriendly: 'pet_friendly',
  },
  content: {
    id: 'id',
    caption: 'caption',
    author: 'author',
    platform: 'platform',
    category: 'category',
    mediaType: 'mediaType',
    isVideo: 'isVideo',
    likes: 'likes',
    comments: 'comments',
    shares: 'share_count',
    views: 'views_count',
    engagement: 'engagement_rate',
    performance: 'performance_tier',
    sentiment: 'sentiment',
    emotion: 'emotion',
    authorFollowers: 'authorFollowers',
    authorCountry: 'authorCountry',
    post_date: 'post_date',
  },
  comments: {
    id: 'id',
    author: 'author',
    text: 'text',
    post: 'post',
    platform: 'platform',
    sentiment: 'sentiment',
    emotion: 'emotion',
    language: 'language',
    toxic: 'toxic',
    spam: 'spam',
    likes: 'likes',
    commentLikes: 'likes',
    date: 'date',
    replies: 'replies',
  },
  brands: { id: 'id', name: 'name', category: 'category', subcategory: 'subcategory', totalDetections: 'total_detections', total_detections: 'total_detections', profiles: 'profiles' },
  hashtags: { id: 'id' },
  teams: { id: 'id', type: 'type', name: 'name', totalMentions: 'total_mentions', total_mentions: 'total_mentions', profiles: 'profiles' },
  channels: { id: 'id', username: 'username', platform: 'platform', category: 'category' },
}

// Helper to get column key from backend sort field
export function getColumnKeyFromSortField(dataSource: DataSourceId, sortField: string): string {
  const map = COLUMN_TO_SORT_FIELD_MAP[dataSource]
  // Find the column key that maps to this sort field (prefer non-underscore keys)
  for (const [columnKey, field] of Object.entries(map)) {
    if (field === sortField && !columnKey.includes('_')) {
      return columnKey
    }
  }
  // Fallback: return sortField as-is if no mapping found
  return sortField
}
